import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState, useEffect, useRef } from 'react'
import { useQuery, useAction, useMutation } from 'convex/react'
import { api } from 'convex/_generated/api'
import type { Id } from 'convex/_generated/dataModel'
import { SongAudioPlayer, AudioPlayerErrorBoundary } from '@/components/SongAudioPlayer'
import { usePostHog } from '@/hooks/usePostHog'
import { Plus, X } from 'lucide-react'
import { useNativeAudioPlayer } from '@/hooks/useNativeAudioPlayer'

export const Route = createFileRoute('/app/songs/$generationId/create-flashcards')({
  component: CreateFlashcardsFromSong,
})

interface FlashcardCandidate {
  frontSpanish: string[]
  backEnglish: string[]
  cardType: 'word' | 'phrase' | 'lyrics'
  timestamp?: number
  tags?: string[]
  previewId: string
}

function CreateFlashcardsFromSong() {
  const { generationId } = Route.useParams()
  const navigate = useNavigate()

  // Load song data
  const song = useQuery(api.tempolor.getById, {
    generationId: generationId as Id<'temporGenerations'>,
  })

  // Fetch audio URLs using three-tier fallback (same pattern as SongDetailPanel)
  const itemUrls = useQuery(
    api.tempolor.getItemStorageUrls,
    song?._id ? { generationId: song._id } : 'skip'
  )
  const storageUrl = useQuery(
    api.tempolor.getStorageUrl,
    song?.storageId ? { storageId: song.storageId } : 'skip'
  )
  const audioUrl = itemUrls?.mp3Url || storageUrl || song?.audioUrl || null

  // Audio ref for controlling playback
  const audioRef = useRef<HTMLAudioElement>(null)
  const [currentPhrase, setCurrentPhrase] = useState<string>('')

  // Use unified audio player hook for coordination
  const { play, seek } = useNativeAudioPlayer({
    audioRef,
    audioId: generationId || 'create-flashcards',
    audioUrl: audioUrl || '',
  })

  // Load user's decks
  const user = useQuery(api.users.current)
  const decks = useQuery(
    api.flashcards.getDecks,
    user ? { userId: user._id } : 'skip'
  )

  // Actions and mutations
  const extractFlashcards = useAction(api.flashcards.extractFlashcardsFromSong)
  const createFlashcards = useMutation(api.flashcards.createFlashcardsFromSong)
  const createDeck = useMutation(api.flashcards.createDeck)
  const { trackEvent } = usePostHog()

  // State
  const [selectedDeckId, setSelectedDeckId] = useState<Id<'flashcardDecks'> | null>(null)
  const [extractedCandidates, setExtractedCandidates] = useState<FlashcardCandidate[]>([])
  const [selectedCandidates, setSelectedCandidates] = useState<Set<string>>(new Set())
  const [isExtracting, setIsExtracting] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [extractionError, setExtractionError] = useState<string | null>(null)
  const [isCreateDeckModalOpen, setIsCreateDeckModalOpen] = useState(false)
  const [newDeckName, setNewDeckName] = useState('')
  const [isCreatingDeck, setIsCreatingDeck] = useState(false)

  // Auto-select first deck when decks load
  useEffect(() => {
    if (decks && decks.length > 0 && !selectedDeckId) {
      setSelectedDeckId(decks[0]._id)
    }
  }, [decks, selectedDeckId])

  // Extract candidates on mount
  useEffect(() => {
    if (!song || extractedCandidates.length > 0 || isExtracting) return

    setIsExtracting(true)
    setExtractionError(null)
    extractFlashcards({
      generationId: generationId as Id<'temporGenerations'>,
    })
      .then((candidates) => {
        const typedCandidates = candidates as FlashcardCandidate[]
        setExtractedCandidates(typedCandidates)
        // Select all by default
        setSelectedCandidates(new Set(typedCandidates.map((c) => c.previewId)))
      })
      .catch((error) => {
        console.error('Failed to extract flashcards:', error)
        setExtractionError(error.message || 'Failed to extract lyrics')
      })
      .finally(() => setIsExtracting(false))
  }, [song, extractedCandidates.length, isExtracting, extractFlashcards, generationId])

  const handleCreateDeck = async () => {
    if (!user || !newDeckName.trim()) return

    setIsCreatingDeck(true)
    try {
      const deckId = await createDeck({
        userId: user._id,
        name: newDeckName.trim(),
      })

      // Auto-select the newly created deck
      setSelectedDeckId(deckId)
      setIsCreateDeckModalOpen(false)
      setNewDeckName('')
    } catch (error) {
      console.error('Failed to create deck:', error)
      alert('Failed to create deck. Please try again.')
    } finally {
      setIsCreatingDeck(false)
    }
  }

  const handleCreate = async () => {
    if (!selectedDeckId || !user || selectedCandidates.size === 0) return

    const selectedFlashcards = extractedCandidates
      .filter((c) => selectedCandidates.has(c.previewId))
      .map(({ frontSpanish, backEnglish, cardType, tags }) => ({
        frontSpanish,
        backEnglish,
        cardType,
        tags: tags || [],
        priority: 5,
      }))

    setIsCreating(true)
    try {
      const result = await createFlashcards({
        userId: user._id,
        deckId: selectedDeckId,
        sourceSongId: generationId as Id<'temporGenerations'>,
        flashcards: selectedFlashcards,
      })

      // Track flashcard creation
      trackEvent('flashcard_created', {
        cardType: 'lyrics', // All flashcards from songs are lyrics type
        count: result.count,
      })

      navigate({ to: '/app/decks' })
    } catch (error) {
      console.error('Failed to create flashcards:', error)
      alert('Failed to create flashcards. Please try again.')
    } finally {
      setIsCreating(false)
    }
  }

  // Seek to timestamp in audio
  const seekToTimestamp = (timestamp: number, text: string) => {
    if (audioRef.current) {
      seek(timestamp)
      play()
      setCurrentPhrase(text)
    }
  }

  const toggleCandidate = (previewId: string) => {
    const newSet = new Set(selectedCandidates)
    if (newSet.has(previewId)) {
      newSet.delete(previewId)
    } else {
      newSet.add(previewId)
    }
    setSelectedCandidates(newSet)
  }

  const toggleAll = () => {
    if (selectedCandidates.size === extractedCandidates.length) {
      setSelectedCandidates(new Set())
    } else {
      setSelectedCandidates(new Set(extractedCandidates.map((c) => c.previewId)))
    }
  }

  if (!song) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-sky-blue font-mono">Loading song...</div>
      </div>
    )
  }

  if (isExtracting) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-terminal-green font-mono">Extracting lyrics...</div>
      </div>
    )
  }

  if (extractionError) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <h1 className="text-2xl font-bold text-red-500 mb-4">Extraction Failed</h1>
        <p className="text-white mb-4">{extractionError}</p>
        <button
          onClick={() => navigate({ to: '/app/songs' })}
          className="px-4 py-2 border border-sky-blue text-sky-blue font-mono rounded hover:bg-sky-blue/10"
        >
          [BACK TO SONGS]
        </button>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <h1 className="text-2xl font-bold text-terminal-green font-mono mb-2">
        [CREATE FLASHCARDS FROM SONG]
      </h1>
      <h2 className="text-lg text-sky-blue font-mono mb-6">
        {song.title || 'Untitled Song'}
      </h2>

      {/* Audio Player */}
      {audioUrl && (
        <AudioPlayerErrorBoundary>
          <SongAudioPlayer
            ref={audioRef}
            audioUrl={audioUrl}
            lyricsSections={song.lyricsSections}
            currentPhrase={currentPhrase}
            onTimeUpdate={(time) => {
              // Optional: Update current phrase based on time
              if (song.lyricsSections) {
                const section = song.lyricsSections.find(
                  s => time >= s.start && time < s.end
                )
                if (section) {
                  setCurrentPhrase(section.text)
                }
              }
            }}
          />
        </AudioPlayerErrorBoundary>
      )}

      {/* Deck Selection */}
      <div className="mb-6 bg-black border-2 border-sky-blue p-4">
        <label className="block text-sky-blue font-mono font-bold text-sm mb-3">
          [SELECT DECK]:
        </label>

        <div className="space-y-2 mb-3">
          {decks && decks.length > 0 ? (
            decks.map((deck: { _id: Id<'flashcardDecks'>; name: string }) => (
              <label
                key={deck._id}
                className={`flex items-center gap-3 p-3 border-2 rounded cursor-pointer transition-colors ${
                  selectedDeckId === deck._id
                    ? 'border-terminal-green bg-terminal-green/10'
                    : 'border-sky-blue/30 hover:border-sky-blue/60'
                }`}
              >
                <input
                  type="radio"
                  name="deck"
                  checked={selectedDeckId === deck._id}
                  onChange={() => setSelectedDeckId(deck._id)}
                  className="w-4 h-4 text-terminal-green"
                />
                <span className="font-mono text-trail-tan flex-1">{deck.name}</span>
              </label>
            ))
          ) : (
            <div className="text-trail-tan/50 font-mono text-sm p-3 border border-dashed border-sky-blue/30 rounded text-center">
              No decks yet. Create one below!
            </div>
          )}
        </div>

        {/* Create Deck Button */}
        <button
          onClick={() => setIsCreateDeckModalOpen(true)}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-terminal-green/50 text-terminal-green hover:border-terminal-green hover:bg-terminal-green/5 font-mono font-bold rounded transition-colors"
        >
          <Plus className="w-5 h-5" />
          [CREATE NEW DECK]
        </button>
      </div>

      {/* Flashcard Candidates */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-sky-blue font-mono">
            [EXTRACTED PHRASES ({extractedCandidates.length})]
          </h3>
          <button
            onClick={toggleAll}
            className="px-3 py-1 text-xs font-mono border border-trail-tan text-trail-tan hover:bg-trail-tan/10 rounded"
          >
            {selectedCandidates.size === extractedCandidates.length
              ? '[DESELECT ALL]'
              : '[SELECT ALL]'}
          </button>
        </div>

        <div className="space-y-2">
          {extractedCandidates.map((candidate) => (
            <div
              key={candidate.previewId}
              className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                selectedCandidates.has(candidate.previewId)
                  ? 'border-terminal-green bg-terminal-green/5'
                  : 'border-sky-blue/30 hover:border-sky-blue/60'
              }`}
              onClick={() => toggleCandidate(candidate.previewId)}
            >
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedCandidates.has(candidate.previewId)}
                  onChange={() => {}}
                  onClick={(e) => e.stopPropagation()}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="font-medium text-terminal-green font-mono">
                    {candidate.frontSpanish.join(' ')}
                  </div>
                  <div className="text-sm text-sky-blue font-mono mt-1">
                    {candidate.backEnglish.join(' ')}
                  </div>
                  {candidate.timestamp !== undefined && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        seekToTimestamp(
                          candidate.timestamp!,
                          candidate.frontSpanish.join(' ')
                        )
                      }}
                      className="text-xs text-sky-blue font-mono mt-1 hover:text-terminal-green hover:underline transition-colors inline-flex items-center gap-1"
                    >
                      🎵 @ {candidate.timestamp.toFixed(3)}s
                    </button>
                  )}
                </div>
              </label>
            </div>
          ))}
        </div>

        {extractedCandidates.length === 0 && (
          <div className="text-center py-8 border-2 border-yellow-500/50 bg-yellow-900/20 rounded-lg">
            <p className="text-yellow-400 font-mono">
              No phrases extracted. The song may not have alternating lyrics.
            </p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-4">
        <button
          onClick={handleCreate}
          disabled={!selectedDeckId || selectedCandidates.size === 0 || isCreating}
          className="px-4 py-2 bg-terminal-green/10 hover:bg-terminal-green/20 text-terminal-green border-2 border-terminal-green font-mono font-bold rounded transition-colors disabled:bg-gray-800 disabled:text-gray-500 disabled:border-gray-600 disabled:cursor-not-allowed"
        >
          {isCreating
            ? '[CREATING...]'
            : `[CREATE ${selectedCandidates.size} FLASHCARD${
                selectedCandidates.size !== 1 ? 'S' : ''
              }]`}
        </button>

        <button
          onClick={() => navigate({ to: '/app/songs' })}
          className="px-4 py-2 border-2 border-sky-blue text-sky-blue font-mono font-bold rounded hover:bg-sky-blue/10 transition-colors"
        >
          [CANCEL]
        </button>
      </div>

      {/* Create Deck Modal */}
      {isCreateDeckModalOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border-2 border-sky-blue rounded-lg max-w-md w-full p-6">
            {/* Modal Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-sky-blue font-mono tracking-wider">
                [CREATE NEW DECK]
              </h3>
              <button
                onClick={() => {
                  setIsCreateDeckModalOpen(false)
                  setNewDeckName('')
                }}
                className="text-trail-tan hover:text-terminal-green transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="space-y-4">
              <div>
                <label className="block text-trail-tan font-mono text-sm mb-2">
                  Deck Name:
                </label>
                <input
                  type="text"
                  value={newDeckName}
                  onChange={(e) => setNewDeckName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newDeckName.trim()) {
                      handleCreateDeck()
                    }
                  }}
                  placeholder="e.g., Spanish Verbs, Travel Phrases..."
                  className="w-full px-3 py-2 bg-black border-2 border-trail-tan text-terminal-green font-mono rounded focus:border-terminal-green focus:outline-none"
                  autoFocus
                />
              </div>

              {/* Modal Actions */}
              <div className="flex gap-3">
                <button
                  onClick={handleCreateDeck}
                  disabled={!newDeckName.trim() || isCreatingDeck}
                  className="flex-1 px-4 py-2 bg-terminal-green/10 hover:bg-terminal-green/20 text-terminal-green border-2 border-terminal-green font-mono font-bold rounded transition-colors disabled:bg-gray-800 disabled:text-gray-500 disabled:border-gray-600 disabled:cursor-not-allowed"
                >
                  {isCreatingDeck ? '[CREATING...]' : '[CREATE DECK]'}
                </button>
                <button
                  onClick={() => {
                    setIsCreateDeckModalOpen(false)
                    setNewDeckName('')
                  }}
                  className="px-4 py-2 border-2 border-sky-blue text-sky-blue font-mono font-bold rounded hover:bg-sky-blue/10 transition-colors"
                >
                  [CANCEL]
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
